import { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withRouter, generatePath } from 'react-router-dom';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import styled from 'styled-components';
import { Set as ImmutableSet, Map as ImmutableMap } from 'immutable';
import memoize from 'transmute/memoize';
import partial from 'transmute/partial';
import { NavMarker } from 'react-rhumb';
import getIn from 'immutable-less/transmute/getIn';
import getComponentName from 'react-utils/getComponentName';
import PostMessageTypes from 'ui-addon-automation-embed/PostMessageTypes';
import { sendMessageToParent } from 'ui-addon-automation-embed/embedUtils';
import UIButton from 'UIComponents/button/UIButton';
import UIFlex from 'UIComponents/layout/UIFlex';
import uniqueIdForObject from 'automation-ui-core/utils/uniqueIdForObject';
import { basePaths } from 'automation-ui-canvas/constants/Routes';
import { getIsCanvasVirtualRenderingDisabled } from 'automation-ui-canvas/selectors/platformFlowsApplicationSelectors';
import ActionConfigComponents from 'automation-ui-canvas/action-features/ActionConfigComponents';
import ActionConfigBasePaths from 'automation-ui-action-features/actions/ActionConfigBasePaths';
import { basePathToActionMetadataConfig } from 'automation-ui-action-features/actions/ActionMetadataConfigUtils';
import * as SpecialActionIds from 'automation-ui-flow-core/canvas/SpecialActionIds';
import { parseLocation, getFlowRoute, getFlowEditRoute } from 'automation-ui-routing/utils';
import { wireAuthConsumer } from 'automation-ui-core/context/AuthContext';
import * as Scopes from 'automation-ui-canvas/constants/Scopes';
import { getIsEnabled, getObjectTypeId, getActionOutputsForTargetActionById, getVersion } from 'automation-ui-canvas/flow/records/Flow/flowUtils';
import * as SourceApps from 'automation-ui-source-apps/constants/SourceApps';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import { trackEvent } from 'automation-ui-canvas/utils/trackingUtils';
import TrackableButton from 'automation-ui-usage-tracking/components/TrackableButton';
import { getFlowValidation, getFlowValidationObjectRequestStatus } from 'automation-ui-canvas/selectors/flowSelectors';
import { updateFlowLocally, requestFlowValidationObject } from 'automation-ui-canvas/actions/FlowActions';
import * as CanvasIdentifiers from 'automation-ui-flow-core/canvas/CanvasIdentifiers';
import { getPqlConfigForFlowObjectTypeId } from 'automation-ui-action-features/access/PqlModalConfigs';
import { attachWrappedComponent } from 'automation-ui-core/decoratorUtils';
import EmbeddedCreateFlowButton from 'automation-ui-canvas/components/canvas/EmbeddedCreateFlowButton';
import { atomicQueryUpdate } from 'automation-ui-routing/historyUtils';
import { getEmbedId, getIsEmbedded, getSourceApp } from 'automation-ui-canvas/selectors/embedSelectors';
import EmbeddedUpgradeButton from 'automation-ui-canvas/components/canvas/EmbeddedUpgradeButton';
import ReadOnlyBanner from 'automation-ui-canvas/components/common/ReadOnlyBanner';
import { CARD_WIDTH_PADDING, CARD_WIDTH } from 'automation-ui-flow-core/canvas/graphConstants';
import ActivationToggle from 'automation-ui-canvas/components/embed/ActivationToggle';
import WorkflowCard from 'automation-ui-canvas/components/canvas/WorkflowCard';
import PannableDecorator from 'automation-ui-canvas/decorators/PannableDecorator';
import ZoomableDecorator from 'automation-ui-canvas/decorators/ZoomableDecorator';
import { getHighlightedActionIds } from 'automation-ui-canvas/selectors/platformFlowsApplicationSelectors';
import * as ActionTypes from 'automation-ui-action-features/constants/ActionTypes';
import { fetchBulkProcessingStatus } from 'automation-ui-canvas/data/api/FlowsApi';
import { setBulkProcessingStatus, setBulkProcessingPollingStatus } from 'automation-ui-canvas/actions/PlatformFlowsApplicationActions';
import { BULK_ENROLL } from 'automation-ui-canvas/constants/BulkProcessingTypes';
import { getShouldPollBulkProcessing } from 'automation-ui-canvas/selectors/platformFlowsApplicationSelectors';
import { reportError } from 'automation-ui-core/errors/utils/reportError';
import { queryAllExtensionDefinitions } from 'automation-ui-action-features/actions/extension/extensionQueries';
import { isGoToActionId } from 'automation-ui-action-features/records/recordsUtils';
import Action from 'automation-ui-action-features/records/Action';
import FlowCanvasNode from 'automation-ui-canvas/components/canvas/FlowCanvasNode';
import { wireActionOutputs } from 'automation-ui-action-features/queries/actionOutputQueries';
import { queryObjectsInActionsPolling } from 'automation-ui-canvas/action-features/objects-in-action/ObjectsInActionQueries';
import { UNINITIALIZED } from 'redux-request-state/requestStates';
import ConnectorButton from 'automation-ui-canvas/components/canvas/ConnectorButton';
import { wireTemplate } from 'automation-ui-canvas/queries/templateQueries';
import { wireFlowById, wireUpdateFlow, defaultErrorHandlingForUpdateFlow } from 'automation-ui-canvas/queries/flowQueries';
import { EDIT_ACTIONS, EDIT_ENROLLMENT } from 'automation-ui-permissions/feature-permissions/constants/WorkflowManagementFeatures';
import { withFeaturePermissions } from 'automation-ui-permissions/feature-permissions/decorators/FeaturePermissionsDecorator';
import { View } from 'foundations-components/components/View';
import UILoadingOverlay from 'UIComponents/loading/UILoadingOverlay';
import UILockBadge from 'UIComponents/badge/UILockBadge';
import { getPreviousActionId } from 'automation-ui-action-features/records/recordsUtils';
import { EVENT_ENROLLMENT_CONFIG, SCHEDULED_ENROLLMENT_CONFIG, LIST_ENROLLMENT_CONFIG, TRIGGER_SELECTION_ENROLLMENT_CONFIG } from 'automation-ui-canvas/constants/AbsolutePaths';
import { getEnrollmentConfigRoute } from 'automation-ui-canvas/utils/absoluteRoutingUtils';
import { ReviewOption } from 'automation-ui-canvas/components/flow-header/menu-bar/menuOptions/ReviewOption';
import { OPEN_QUERY_PARAM } from 'automation-ui-canvas/components/flow-header/menu-bar/MenuBar';
import withFlpAccess from 'automation-ui-permissions/flp/hocs/withFlpAccess';
import { getZoomLevel } from 'automation-ui-canvas/selectors/localStorageSelectors';
import EndMarker from 'automation-ui-canvas/components/common/EndMarker';
import { NEXT_ACTION_RECOMMENDER, COLLABORATION_GENERATE_ACTION, COLLABORATION_GENERATE_ACTION_ELIGIBLE_CUSTOMERS } from 'automation-ui-canvas/constants/Gates';
import NextRecommendedAction from 'automation-ui-canvas/components/canvas/next-action-recommender/NextRecommendedAction';
import CollaborationGenActionsNode from 'automation-ui-canvas/components/canvas/collaborate-generate-actions-card/CollaborateGenerateActionsNode';
import { getIsEnrollmentEdge } from 'automation-ui-canvas/utils/getIsEnrollmentEdge';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const EMBED_LANG_KEY = 'automation.embed.flowCanvasContainer';
const PROCESSING = 'PROCESSING';
const FAILURE_LIMIT = 4;
const StyledUIFlex = styled(UIFlex).withConfig({
  displayName: "FlowCanvasFullDecorator__StyledUIFlex",
  componentId: "pox59d-0"
})(["text-align:-webkit-center;&& > span{margin:auto;}button{margin:auto;width:unset;}"]);
const StyledCreateFlowButton = styled.div.withConfig({
  displayName: "FlowCanvasFullDecorator__StyledCreateFlowButton",
  componentId: "pox59d-1"
})(["padding:0 20px 10px;"]);
const StyledCanvas = styled.div.withConfig({
  displayName: "FlowCanvasFullDecorator__StyledCanvas",
  componentId: "pox59d-2"
})(["height:100%;"]);
const StyledRoot = styled.div.withConfig({
  displayName: "FlowCanvasFullDecorator__StyledRoot",
  componentId: "pox59d-3"
})(["height:100%;"]);
function getComposedCanvas(isEmbedded) {
  if (!isEmbedded) {
    return PannableDecorator(ZoomableDecorator({
      // TODO(TS_BUG)
      // @ts-expect-error ZoomableDecorator expects StyledCanvas to have its required props, but it's just a div
      // and the required props aren't wired until ComposedCanvas is used in FlowCanvasBase. Not sure if real error or not
      WrappedComponent: StyledCanvas,
      shouldRenderUndoRedoButtons: true
    }));
  }
  return StyledCanvas;
}
export default function FlowCanvasFullDecorator(WrappedComponent, centerOnMount) {
  const componentName = getComponentName(WrappedComponent);
  class FlowCanvasFull extends Component {
    constructor(...args) {
      super(...args);
      this.state = {
        shouldEmbedCreateFlowButtonShow: false,
        shouldEmbedActivateFlowButtonShowIfDisabled: true,
        isActivateFlowInProgress: false,
        showLoadingOverlay: false
      };
      this.isNew = this.props.isNew;
      this.ComposedCanvas = getComposedCanvas(this.props.isEmbedded);
      this.terminateBulkProcessingPolling = () => {
        clearInterval(this.pollProcessingStatusId);
        this.pollProcessingStatusId = -1;
      };
      this.failureCount = 0;
      this.toggleAddEmptyActionToFlow = (selectedEdgeId, forceRemove = false) => {
        const {
          flow,
          dispatch
        } = this.props;
        if (!flow) {
          return;
        }
        const emptyAction = new Action({
          actionType: ActionTypes.EMPTY_ACTION,
          actionId: SpecialActionIds.NEW_ACTION
        });
        let newFlow = flow;
        const addAction = !!selectedEdgeId;
        if (addAction && !forceRemove) {
          newFlow = newFlow.removeAction(SpecialActionIds.NEW_ACTION);
          newFlow = newFlow.insertAction({
            action: emptyAction,
            edgeId: selectedEdgeId,
            actionId: SpecialActionIds.NEW_ACTION
          });
        } else {
          newFlow = newFlow.removeAction(SpecialActionIds.NEW_ACTION);
        }
        dispatch(updateFlowLocally(newFlow));
      };
      this.pollBulkProcessingStatus = (flowId, shouldPollBulkProcessing) => {
        const {
          dispatch
        } = this.props;
        // checking for pollProcessingStatusId to prevent multiple polls on prop change
        if (shouldPollBulkProcessing && this.pollProcessingStatusId === -1) {
          this.pollProcessingStatusId = setInterval(() => {
            fetchBulkProcessingStatus(flowId, PROCESSING).then(res => {
              this.failureCount = 0;
              const nextBulkProcessingStatus = this.parseBulkProcessingResults(res);
              dispatch(
              // TODO(TS_BUG)
              // @ts-expect-error clearly is not the bulkProcessingStatus but I don't want to mess with it
              setBulkProcessingStatus(flowId, nextBulkProcessingStatus));
            }).catch(error => {
              if (++this.failureCount === FAILURE_LIMIT) {
                // Not sure how to handle this other than report it to Sentry.
                reportError({
                  error
                });
              }
            });
          }, 2000);
        } else if (!shouldPollBulkProcessing) {
          this.terminateBulkProcessingPolling();
        }
      };
      this.parseBulkProcessingResults = results => {
        const actionsBulkProcessing = [];
        let enrollmentIsBulkProcessing = false;
        let shouldPollBulkProcessing = false;
        results.forEach(result => {
          const actionId = getIn(['actionId'], result);
          const processingType = getIn(['metadata', 'bulkProcessingType'], result);
          if (processingType === BULK_ENROLL) {
            enrollmentIsBulkProcessing = true;
          } else if (actionId) {
            actionsBulkProcessing.push(actionId);
          }
        });
        if (actionsBulkProcessing.length !== 0 || enrollmentIsBulkProcessing) {
          shouldPollBulkProcessing = true;
        }
        return ImmutableMap({
          actionsBulkProcessing: ImmutableSet(actionsBulkProcessing),
          enrollmentIsBulkProcessing,
          shouldPollBulkProcessing
        });
      };
      this.getComposedProps = () => {
        const {
          flow,
          isEmbedded,
          columnWidth,
          canvasScrollElementRef,
          highlightedActionIds,
          isMulti,
          flowValidation,
          showEnrollmentCardInEmbed,
          zoomLevel
        } = this.props;
        return {
          flow,
          isEmbedded,
          isMulti,
          columnWidth,
          canvasScrollElementRef,
          highlightedActionIds,
          flowValidation,
          // passed to force a refresh of the canvas
          onRenderCanvasNode: this.onRenderCanvasNode,
          onSelectAction: this.onSelectAction,
          TopContent: this.renderTopContent(),
          BottomContent: isEmbedded ? this.renderEmbeddedBottomContent() : null,
          ComposedCanvas: this.ComposedCanvas,
          goToEditNodeId: null,
          onRenderConnectorButton: this.onRenderConnectorButton,
          onRenderEndMarker: this.onRenderEndMarker,
          showEnrollmentCard: !isEmbedded || showEnrollmentCardInEmbed,
          centerOnMount,
          zoomLevel
        };
      };
      this.preloadActionConfigChunks = (props = this.props) => {
        const {
          flow
        } = props;
        if (!flow) {
          return;
        }
        flow.actions.map(action => action.actionType).toSet().forEach(actionType => {
          const LoadableConfig = ActionConfigComponents[actionType];
          if (LoadableConfig) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            LoadableConfig.preload();
          }
        });
      };
      this.shouldHideEmptyFlow = (props = this.props) => {
        const {
          flow
        } = props;
        const {
          shouldEmbedCreateFlowButtonShow
        } = this.state;
        if (!flow) {
          return false;
        }
        return !flow.firstActionId && shouldEmbedCreateFlowButtonShow && !this.isNew;
      };
      this.handleGetActionOutputsForTargetActionById = (targetActionId, isNew) => getActionOutputsForTargetActionById({
        flow: this.props.flow,
        allOutputsByActionTypeId: this.props.allOutputsByActionTypeId,
        instanceOutputsByActionId: this.props.instanceOutputsByActionId,
        targetActionId,
        isNewAction: isNew
      });
      this.handleShouldEmbedCreateFlowButtonShow = flow => {
        const {
          isEmbedded,
          showEnrollmentCardInEmbed
        } = this.props;
        const {
          shouldEmbedCreateFlowButtonShow
        } = this.state;
        if (isEmbedded && flow && !showEnrollmentCardInEmbed && !flow.firstActionId && !shouldEmbedCreateFlowButtonShow && !this.isNew) {
          this.setState({
            shouldEmbedCreateFlowButtonShow: true
          });
        }
      };
      this.toggleSelectedEdgeId = (edgeId, remove) => {
        const {
          history,
          flowId: activeFlowId
        } = this.props;
        if (remove) {
          atomicQueryUpdate({}, ['edgeId', 'activeFlowId'], history, true);
        } else {
          // make sure to also remove the actionId
          atomicQueryUpdate({
            edgeId,
            activeFlowId
          }, ['actionId'], history, true);
        }
      };
      this.toggleEmbeddedSelectedActionId = (actionId, remove, tab) => {
        const {
          history,
          flowId: activeFlowId
        } = this.props;
        if (remove) {
          atomicQueryUpdate({}, ['actionId', 'activeFlowId'], history, true);
        } else {
          // make sure to also remove the edgeId
          atomicQueryUpdate({
            actionId,
            activeFlowId,
            tab
          }, ['edgeId'], history, true);
        }
      };
      this.onClickNavigateToFullApp = flowId => event => {
        const {
          portalId,
          flow,
          embedId,
          sourceApp
        } = this.props;
        const isFlowEnabled = getIsEnabled(flow);
        const search = isFlowEnabled ? '' : `?${OPEN_QUERY_PARAM}=${ReviewOption.id}`;
        const workflowsURL = `https://${window.location.host}/workflows/${portalId}${getFlowEditRoute(flowId)}${search}`;
        trackEvent(Trackables.OPEN_WORKFLOWS_APP_FROM_EMBED, {
          sourceApp,
          type: flow.objectTypeId
        });
        sendMessageToParent({
          type: PostMessageTypes.SET_EMBED_ORIGIN_URL_SESSION_STORAGE_KEY,
          embedId
        });
        sendMessageToParent({
          type: PostMessageTypes.NAVIGATE_TO_WORKFLOWS_APP,
          embedId,
          url: workflowsURL,
          openInNewTab: event.metaKey
        });
      };
      this.onSelectAction = (id, event = {}) => {
        const {
          tab
        } = event;
        const {
          flow,
          history,
          flowId,
          embedId,
          isEmbedded,
          activeFlowId,
          isMulti
        } = this.props;
        let configRoute = '';
        let configId = null;
        if (id !== CanvasIdentifiers.FIRST_NODE) {
          const actionId = Number(id);
          const action = flow.getActionById(actionId);
          const actionType = action ? action.getActionType() : null;
          configRoute = actionType ? ActionConfigBasePaths[actionType] : null;
          configId = actionId;
        } else {
          configId = SpecialActionIds.ENROLLMENT;
        }
        const isActiveFlow = activeFlowId === flowId;
        if (!isMulti || isActiveFlow) {
          // In some cases when going from a "new-action" to an existing action
          // the config component does not unmount so we need to remove the
          // new-action and re-connect the actions via this toggleAddEmptyActionToFlow call
          // https://git.hubteam.com/HubSpot/AutomationTeam/issues/4445
          this.toggleAddEmptyActionToFlow(null);
        }
        if (isEmbedded) {
          sendMessageToParent({
            type: PostMessageTypes.SELECT_ACTION,
            flowId,
            embedId,
            configId,
            actionNameRoute: configRoute,
            tab
          });
          this.toggleEmbeddedSelectedActionId(id, false, tab);
          return;
        }
        const editActionsPath = `${getFlowRoute(flowId)}/${basePaths.EDIT}/${basePaths.PLATFORM_FLOW_CONFIGS}`;
        let pathname = `${editActionsPath}/${id}/${configRoute}`;
        if (id === CanvasIdentifiers.FIRST_NODE) {
          pathname = getEnrollmentConfigRoute({
            flow
          });
        } else if (isGoToActionId(id)) {
          this.toggleSelectedEdgeId(null, true);
          history.replace({
            pathname: `${getFlowRoute(flowId)}/${basePaths.EDIT}`,
            search: history.location.search
          });
          return;
        } else if (id === CanvasIdentifiers.VIRTUAL_ANCHORED_DELAY_NODE) {
          const virtualConfigRoute = ActionConfigBasePaths[ActionTypes.CLASSIC_VIRTUAL_ANCHORED_DELAY];
          pathname = `${editActionsPath}/${id}/${virtualConfigRoute}`;
        }
        this.toggleSelectedEdgeId(null, true);
        const params = new URLSearchParams(history.location.search);
        if (tab) {
          params.set('tab', tab);
          history.replace({
            pathname: `${pathname}`,
            search: params.toString(),
            state: {
              doNotScrollTo: true
            }
          });
        } else {
          history.replace({
            pathname,
            state: {
              doNotScrollTo: true
            },
            search: history.location.search
          });
        }
      };
      this.handleIsNew = (props = this.props) => {
        const {
          flow,
          edgeId,
          activeFlowId,
          flowId
        } = props;
        if (this.isNew && flow && !flow.firstActionId && (!edgeId || activeFlowId !== flowId) // ensure the edge isn't selected when programatically clicking based on isNew
        ) {
          this.onClickAddControl(CanvasIdentifiers.FIRST_NODE_EDGE);
        }
      };
      this.handleEmbedOnClickAddControl = (edgeId, removeEdgeId) => {
        const {
          flowId,
          embedId
        } = this.props;
        sendMessageToParent({
          type: PostMessageTypes.ADD_CONTROL,
          flowId,
          embedId,
          edgeId
        });
        if (removeEdgeId) {
          sendMessageToParent({
            type: PostMessageTypes.CLOSE_SIDEPANEL,
            embedId
          });
          this.toggleSelectedEdgeId(null, true);
        } else {
          this.toggleSelectedEdgeId(edgeId, false);
        }
      };
      this.onClickAddControl = edgeId => {
        const {
          history,
          flowId,
          isEmbedded,
          edgeId: selectedEdgeId,
          activeFlowId,
          isMulti,
          flow
        } = this.props;
        const flowRoute = getFlowRoute(flowId);
        const removeEdgeId = selectedEdgeId === edgeId && (!isMulti || !activeFlowId || activeFlowId === flowId);
        if (!removeEdgeId) {
          trackEvent(Trackables.ADD_ACTION_CONFIG, {
            objectTypeId: getObjectTypeId(flow),
            flowId // to ensure tracking within embed
          });
        }
        const baseNewRoute = `${flowRoute}/${basePaths.EDIT}`;
        const newRoute = removeEdgeId ? baseNewRoute : `${baseNewRoute}/${basePaths.PLATFORM_FLOW_CONFIGS}/${SpecialActionIds.NEW_ACTION}?edgeId=${edgeId}`;
        if (isEmbedded) {
          this.handleEmbedOnClickAddControl(edgeId, removeEdgeId);
        } else {
          history.replace(newRoute);
        }
      };
      this.onRenderEndMarker = ({
        edgeId,
        isMultiEndMarkerWorkflow
      }) => {
        const {
          flowId,
          flow,
          zoomLevel,
          isUngated
        } = this.props;
        const nextActionRecommendationUngated = isUngated(NEXT_ACTION_RECOMMENDER);
        const isEnrollmentEdge = getIsEnrollmentEdge(edgeId);
        const isCollabGenActionsEnabled = isUngated(COLLABORATION_GENERATE_ACTION);
        const isCollaborationGenerateActionEligibleCustomer = isUngated(COLLABORATION_GENERATE_ACTION_ELIGIBLE_CUSTOMERS);
        return /*#__PURE__*/_jsxs(_Fragment, {
          children: [isEnrollmentEdge && isCollabGenActionsEnabled && isCollaborationGenerateActionEligibleCustomer && /*#__PURE__*/_jsx(CollaborationGenActionsNode, {
            edgeId: edgeId,
            flow: flow,
            flowId: flowId,
            zoomLevel: zoomLevel
          }), !isEnrollmentEdge && nextActionRecommendationUngated && /*#__PURE__*/_jsx(NextRecommendedAction, {
            edgeId: edgeId,
            flow: flow,
            flowId: flowId,
            isMulti: isMultiEndMarkerWorkflow,
            zoomLevel: zoomLevel
          }), /*#__PURE__*/_jsx(EndMarker, {
            flow: flow,
            edgeId: edgeId,
            zoomLevel: zoomLevel
          })]
        });
      };
      this.onRenderConnectorButton = ({
        edge,
        readOnly,
        isMulti,
        showEdgeTooltips
      }) => {
        const {
          flowId
        } = this.props;
        return /*#__PURE__*/_jsx(ConnectorButton, {
          edge: edge,
          isMulti: isMulti,
          flowId: flowId,
          showEdgeTooltips: showEdgeTooltips,
          "data-test-id": `single-connector-${edge.getId()}`,
          onClick: partial(this.onClickAddControl, edge.getId()),
          disabled: readOnly,
          tooltip: readOnly && /*#__PURE__*/_jsx(FormattedMessage, {
            message: "automation.common.readOnlyTooltip"
          }),
          placement: "left",
          "aria-label": I18n.text('edit.actions.addAction')
        });
      };
      this.isNodeHighlighted = nodeId => {
        const {
          highlightedActionIds
        } = this.props;
        return highlightedActionIds && highlightedActionIds.has(nodeId);
      };
      this.onRenderCanvasNode = memoize(({
        id: nodeId,
        width,
        height
      }) => {
        const {
          flow,
          selectedActionId,
          showEnrollmentCardInEmbed,
          isEmbedded,
          isMulti,
          activeFlowId,
          flowId,
          isCanvasVirtualRenderingDisabled,
          selectedActionMetadataConfig,
          objectsInActions,
          getFeaturePermission,
          flpAccessData,
          flpAccessError,
          zoomLevel
        } = this.props;
        let isHighlighted = this.isNodeHighlighted(nodeId);
        const isSelected = selectedActionId === nodeId.toString() && !(isMulti && activeFlowId !== flowId);
        let readOnly = true;
        const hasFlpWriteAccess = !flpAccessError && (flpAccessData === null || flpAccessData === void 0 ? void 0 : flpAccessData.hasAccess(EDIT_ENROLLMENT)) && (flpAccessData === null || flpAccessData === void 0 ? void 0 : flpAccessData.hasAccess(EDIT_ACTIONS));
        if (nodeId === CanvasIdentifiers.FIRST_NODE) {
          if (isEmbedded && !showEnrollmentCardInEmbed) {
            return null;
          }
          const {
            hasFeatureAccess: hasEditEnrollmentAccess
          } = getFeaturePermission({
            feature: EDIT_ENROLLMENT
          });
          readOnly = !hasEditEnrollmentAccess || showEnrollmentCardInEmbed;
        } else {
          const {
            hasFeatureAccess: hasEditActionAccess
          } = getFeaturePermission({
            feature: EDIT_ACTIONS
          });
          readOnly = !hasEditActionAccess;
        }
        readOnly || (readOnly = !hasFlpWriteAccess);
        if (nodeId === CanvasIdentifiers.VIRTUAL_ANCHORED_DELAY_NODE) {
          isHighlighted = this.isNodeHighlighted(CanvasIdentifiers.FIRST_NODE);
        }
        if (isGoToActionId(nodeId)) {
          isHighlighted = this.isNodeHighlighted(getPreviousActionId(nodeId));
        }
        return /*#__PURE__*/_jsx(FlowCanvasNode, {
          id: nodeId,
          width: width,
          height: height,
          flow: flow,
          isSelected: isSelected,
          isHighlighted: isHighlighted,
          readOnly: readOnly,
          showIcon: !isMulti,
          isCanvasVirtualRenderingDisabled: isCanvasVirtualRenderingDisabled,
          objectsInActions: objectsInActions,
          setShowLoadingOverlay: loading => this.setState({
            showLoadingOverlay: loading
          }),
          onSelectNode: this.onSelectAction,
          selectedActionMetadataConfig: selectedActionMetadataConfig,
          getActionOutputsForTargetActionById: this.handleGetActionOutputsForTargetActionById,
          zoomLevel: zoomLevel
        });
      },
      /**
       * This advanced memo cache key ensures action
       * cards are memoized whenever possible. This
       * is a necessary optimization to make the
       * virtual renderer smooth for large workflows.
       */
      ({
        id: nodeId
      }) => {
        const {
          flowValidation,
          selectedActionId,
          highlightedActionIds,
          isEmbedded,
          isMulti,
          activeFlowId,
          flowId,
          isCanvasVirtualRenderingDisabled,
          selectedActionMetadataConfig,
          flow,
          objectsInActions,
          isActionOutputsLoading,
          template,
          zoomLevel
        } = this.props;

        // Since updating actions updates the flow
        // version, this is a safe optimization to
        // avoid virtual actions triggering busting
        // this cache
        const flowVersion = flow.getVersion();
        const stagedFlowVersion = flow.stagedVersion;
        const templateId = template && template.templateId;
        const isHighlighted = highlightedActionIds && highlightedActionIds.has(nodeId);
        let isSelected = selectedActionId === nodeId.toString();
        if (isMulti && activeFlowId !== flowId) {
          isSelected = null;
        }
        let selectedActionMetadataConfigForNewAction;
        if (nodeId === SpecialActionIds.NEW_ACTION) {
          selectedActionMetadataConfigForNewAction = selectedActionMetadataConfig;
        }
        const objectsInThisAction = objectsInActions ? objectsInActions.find(delayedAction => delayedAction.actionId === nodeId) : null;
        const objectsInThisActionCount = objectsInThisAction && objectsInThisAction.count;
        return `${nodeId}-${flowVersion}-${uniqueIdForObject(flowValidation)}-${isSelected}-${isHighlighted}-${isEmbedded}-${isMulti}-${activeFlowId}-${flowId}-${isCanvasVirtualRenderingDisabled}-${selectedActionMetadataConfigForNewAction}-${objectsInThisActionCount}-actionOutputsLoading:${isActionOutputsLoading}-${templateId}-stagedFlowVersion:${stagedFlowVersion}-zoomLevel:${zoomLevel}`;
      });
      this.handleRequestFlow = (props = this.props) => {
        const {
          flowId,
          isEmbedded,
          flowQuery
        } = props;
        if (flowId > 0) {
          if (isEmbedded) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            flowQuery.refetch({
              flowId
            });
          }
        }
      };
      this.handleMessage = message => {
        const {
          flowId,
          flow
        } = this.props;
        const {
          data = {}
        } = message;
        switch (data.type) {
          case PostMessageTypes.NEW_MULTI_FLOW_CREATED:
          case PostMessageTypes.NEW_FLOW_CREATED:
            if (flowId === data.flowId) {
              this.isNew = true;
              this.handleIsNew();
            }
            break;
          case PostMessageTypes.NEW_MULTI_UUID_FLOW_CREATED:
            if (flow.uuid === data.uuid) {
              this.isNew = true;
              this.handleIsNew();
            }
            break;
          case PostMessageTypes.REFRESH_FLOW:
            if (!data.edgeId) {
              atomicQueryUpdate({}, ['edgeId', 'activeFlowId', 'actionId'], this.props.history, true);
            }
            if (flowId === data.flowId) {
              if (data.edgeId) {
                this.toggleSelectedEdgeId(data.edgeId, false);
              }
              this.handleRequestFlow();
            }
            break;
          case PostMessageTypes.SELECT_ACTION:
          case PostMessageTypes.ADD_CONTROL:
            {
              if (flowId !== data.flowId) {
                this.isNew = false;
                this.forceUpdate();
              }
              break;
            }
          case PostMessageTypes.CLOSE_SIDEPANEL:
            if (this.isNew) {
              this.isNew = false;
            }
            atomicQueryUpdate({}, ['edgeId', 'actionId'], this.props.history, true);
            break;
          default:
            break;
        }
      };
      this.handleEmbedCreateFlowButtonClicked = () => {
        this.isNew = true;
        this.setState({
          shouldEmbedCreateFlowButtonShow: false
        });
        this.onClickAddControl(CanvasIdentifiers.FIRST_NODE_EDGE);
      };
      this.handleEmbedFlowActivation = () => {
        const {
          flow,
          updateFlow,
          dispatch
        } = this.props;
        this.setState({
          isActivateFlowInProgress: true
        });

        // eslint-disable-next-line promise/catch-or-return,@typescript-eslint/no-floating-promises
        updateFlow({
          sourceApp: SourceApps.WORKFLOWS_APP,
          flow: flow.set('isEnabled', true)
        }).catch(error => defaultErrorHandlingForUpdateFlow({
          error,
          dispatch
        })).then(() => {
          if (this._isMounted) {
            this.setState({
              isActivateFlowInProgress: false,
              shouldEmbedActivateFlowButtonShowIfDisabled: false
            });
          }
        });
      };
      this.handleEmbedFlowDeactivation = () => {
        const {
          flow,
          updateFlow,
          dispatch
        } = this.props;
        this.setState({
          isActivateFlowInProgress: true
        });

        // eslint-disable-next-line promise/catch-or-return,@typescript-eslint/no-floating-promises
        updateFlow({
          sourceApp: SourceApps.WORKFLOWS_APP,
          flow: flow.set('isEnabled', false)
        }).catch(error => defaultErrorHandlingForUpdateFlow({
          error,
          dispatch
        })).then(() => {
          if (this._isMounted) {
            this.setState({
              isActivateFlowInProgress: false,
              shouldEmbedActivateFlowButtonShowIfDisabled: false
            });
          }
        });
      };
      this.handleEmbedToggle = () => {
        const {
          flow
        } = this.props;
        const isFlowEnabled = getIsEnabled(flow) || false;
        if (isFlowEnabled) {
          this.handleEmbedFlowDeactivation();
        } else {
          this.handleEmbedFlowActivation();
        }
      };
      this.renderEmbeddedContentButton = () => {
        const {
          hasWorkflowsAppAccess,
          flow,
          arrPosition,
          sourceApp,
          upgradeModalKey
        } = this.props;
        if (!hasWorkflowsAppAccess) {
          const flowObjectTypeId = getObjectTypeId(flow);
          const config = getPqlConfigForFlowObjectTypeId(flowObjectTypeId);
          const extraProps = {
            uniqueId: `open-in-workflows-cta-${arrPosition}`,
            config,
            screen: `embedded-workflow-open-in-workflows-${sourceApp}`,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: `${EMBED_LANG_KEY}.openInWorkflows.button`
            })
          };
          return /*#__PURE__*/_jsxs(View, {
            as: "span",
            marginBlockStart: "100",
            children: [/*#__PURE__*/_jsx(EmbeddedUpgradeButton, Object.assign({
              use: "link",
              upgradeModalKey: upgradeModalKey,
              size: "small"
            }, extraProps)), /*#__PURE__*/_jsx(UILockBadge, {})]
          });
        }
        const flowId = flow.getFlowId();
        return /*#__PURE__*/_jsx(UIButton, {
          use: "link",
          onClick: this.onClickNavigateToFullApp(flowId),
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `${EMBED_LANG_KEY}.openInWorkflows.button`
          })
        });
      };
      this.renderEmbeddedFlowDisableControl = () => {
        const {
          hasWorkflowsAppAccess,
          flow,
          sourceApp
        } = this.props;
        const {
          shouldEmbedActivateFlowButtonShowIfDisabled,
          isActivateFlowInProgress
        } = this.state;
        if (hasWorkflowsAppAccess || !shouldEmbedActivateFlowButtonShowIfDisabled) {
          return null;
        }
        const objectTypeId = getObjectTypeId(flow);
        return /*#__PURE__*/_jsx(TrackableButton, {
          onClick: this.handleEmbedFlowActivation,
          disabled: isActivateFlowInProgress,
          extraJSONData: {
            objectTypeId,
            sourceApp
          },
          trackableKey: Trackables.CLICK_ACTIVATE_WORKFLOW_FROM_EMBED,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `${EMBED_LANG_KEY}.workflowOff.turnOnLink`
          })
        });
      };
      this.renderEmbeddedTopContent = () => {
        const {
          flow,
          isEmbedded,
          hasWorkflowsAppAccess
        } = this.props;
        const isFlowEnabled = getIsEnabled(flow) || false;
        if (!isEmbedded) {
          return null;
        }
        return /*#__PURE__*/_jsx(ActivationToggle, {
          hasWorkflowsAppAccess: hasWorkflowsAppAccess,
          isFlowEnabled: isFlowEnabled,
          onChange: this.handleEmbedToggle
        });
      };
      this.renderEmbeddedBottomContent = () => {
        return /*#__PURE__*/_jsxs(StyledUIFlex, {
          className: "embed-bottom-content-container",
          direction: "column",
          justify: "center",
          children: [/*#__PURE__*/_jsx("span", {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: `${EMBED_LANG_KEY}.openInWorkflows.cta`
            })
          }), this.renderEmbeddedContentButton()]
        });
      };
      this.renderEmbedCreateFlowButton = () => {
        const {
          flow
        } = this.props;
        const flowObjectTypeId = getObjectTypeId(flow);
        const isFlowEnabled = getIsEnabled(flow) || false;
        const doesWorkflowExist = isFlowEnabled;
        return /*#__PURE__*/_jsx(StyledCreateFlowButton, {
          "data-test-id": "flow-canvas-container-fake-create-button",
          children: /*#__PURE__*/_jsx(EmbeddedCreateFlowButton, {
            doesWorkflowExist: doesWorkflowExist,
            onClick: this.handleEmbedCreateFlowButtonClicked,
            arrPosition: this.props.arrPosition,
            flowObjectTypeId: flowObjectTypeId
          })
        });
      };
      this.renderCollapsedFlow = () => {
        const {
          flowId,
          columnWidth
        } = this.props;
        const cardWidth = Math.min(columnWidth - CARD_WIDTH_PADDING * 2, CARD_WIDTH);
        return /*#__PURE__*/_jsx("div", {
          className: "p-all-5 display-flex justify-center",
          children: /*#__PURE__*/_jsx(WorkflowCard, {
            flowId: flowId,
            onClick: this.onClickNavigateToFullApp,
            width: cardWidth
          })
        });
      };
    }
    componentDidMount() {
      const {
        dispatch,
        flowId,
        edgeId
      } = this.props;
      this._isMounted = true;
      window.addEventListener('message', this.handleMessage);
      this.handleRequestFlow(this.props);
      this.handleIsNew();
      this.handleShouldEmbedCreateFlowButtonShow(this.props.flow);
      this.preloadActionConfigChunks();
      dispatch(setBulkProcessingPollingStatus(flowId, true));
      this.toggleAddEmptyActionToFlow(edgeId);
      this.pollProcessingStatusId = -1;
    }
    componentDidUpdate(prevProps) {
      const {
        flow,
        flowValidationObjectRequestStatus,
        isEmbedded,
        dispatch,
        shouldPollBulkProcessing,
        edgeId: selectedEdgeId,
        activeFlowId,
        isMulti,
        embedId,
        flowId
      } = this.props;
      if (flow !== prevProps.flow) {
        this.preloadActionConfigChunks(this.props);
      }
      if (prevProps.embedId !== embedId || prevProps.flowId !== flowId) {
        this.handleRequestFlow(this.props);
      }

      // This check looks like it was previously added to detect Flow changes
      // without unmounting. The new canvas algorithm modifies the Flow object
      // when there's a staged action being added. This causes it to appear to
      // change even though it hasn't.
      const justLoadedFlow = !prevProps.flow && flow;
      const isDifferentFlow = flow && prevProps.flow && prevProps.flow.flowId !== flow.flowId;
      if (justLoadedFlow || isDifferentFlow) {
        this.handleIsNew(this.props);
      }
      this.handleShouldEmbedCreateFlowButtonShow(flow);
      if (isEmbedded !== prevProps.isEmbedded) {
        this.ComposedCanvas = getComposedCanvas(isEmbedded);
      }
      if (flow) {
        this.pollBulkProcessingStatus(flowId, shouldPollBulkProcessing);
        if (prevProps.flow && flow.getActions() !== prevProps.flow.getActions() && flow.getVersion() > prevProps.flow.getVersion() || isEmbedded && flowValidationObjectRequestStatus === UNINITIALIZED) {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          dispatch(requestFlowValidationObject(flow));
        }
      }
      if (selectedEdgeId !== prevProps.edgeId || getVersion(flow) !== getVersion(prevProps.flow)) {
        const isActiveFlow = activeFlowId === flowId;
        if (!isMulti || isActiveFlow || !selectedEdgeId) {
          this.toggleAddEmptyActionToFlow(selectedEdgeId);
        }
      }
    }
    componentWillUnmount() {
      this._isMounted = false;
      this.terminateBulkProcessingPolling();
      window.removeEventListener('message', this.handleMessage);
    }
    getTriggerSelectionRoute() {
      return generatePath(TRIGGER_SELECTION_ENROLLMENT_CONFIG, {
        flowId: this.props.flowId
      });
    }
    getEventEnrollmentRoute() {
      return generatePath(EVENT_ENROLLMENT_CONFIG, {
        flowId: this.props.flowId
      });
    }
    getListEnrollmentRoute() {
      return generatePath(LIST_ENROLLMENT_CONFIG, {
        flowId: this.props.flowId
      });
    }
    getScheduledEnrollmentRoute() {
      return generatePath(SCHEDULED_ENROLLMENT_CONFIG, {
        flowId: this.props.flowId
      });
    }
    renderTopContent() {
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [this.renderReadOnlyAlert(), this.renderEmbeddedTopContent()]
      });
    }
    renderReadOnlyAlert() {
      const {
        flow
      } = this.props;
      return /*#__PURE__*/_jsx(UIFlex, {
        justify: "center",
        children: /*#__PURE__*/_jsx(ReadOnlyBanner, {
          flow: flow,
          editFeatures: [EDIT_ACTIONS, EDIT_ENROLLMENT]
        })
      });
    }
    render() {
      const {
        showLoadingOverlay
      } = this.state;
      const {
        flow,
        hasWorkflowsAppAccess,
        isEmbedded,
        isMulti
      } = this.props;

      // TODO: handle error fetching flow and add to Raegan
      if (!flow) {
        return null;
      }
      const isFlowEnabled = getIsEnabled(flow) || false;

      // Assume a top-level NavMarker is set for multiflow canvas
      if (isEmbedded && flow.hasBranchActions()) {
        return /*#__PURE__*/_jsxs(StyledRoot, {
          "data-test-id": "flow-canvas-container",
          children: [!isMulti && /*#__PURE__*/_jsx(NavMarker, {
            name: "FLOW_CANVAS"
          }), /*#__PURE__*/_jsx(ActivationToggle, {
            hasWorkflowsAppAccess: hasWorkflowsAppAccess,
            isFlowEnabled: isFlowEnabled,
            onChange: this.handleEmbedToggle
          }), this.renderCollapsedFlow()]
        });
      }
      if (this.shouldHideEmptyFlow()) {
        return /*#__PURE__*/_jsxs("div", {
          "data-test-id": "flow-canvas-container",
          children: [!isMulti && /*#__PURE__*/_jsx(NavMarker, {
            name: "FLOW_CANVAS"
          }), /*#__PURE__*/_jsx(ActivationToggle, {
            hasWorkflowsAppAccess: hasWorkflowsAppAccess,
            isFlowEnabled: isFlowEnabled,
            onChange: this.handleEmbedToggle
          }), this.renderEmbedCreateFlowButton()]
        });
      }
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [showLoadingOverlay && /*#__PURE__*/_jsx(UILoadingOverlay, {
          use: "dots"
        }), !isMulti && /*#__PURE__*/_jsx(NavMarker, {
          name: "FLOW_CANVAS"
        }), /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, this.props, this.getComposedProps()))]
      });
    }
  }
  FlowCanvasFull.defaultProps = {
    isMulti: false,
    readOnly: false,
    showEnrollmentCardInEmbed: false,
    columnWidth: 260,
    canvasScrollElementRef: null
  };
  const ConnectedFlowCanvasFull = compose(withFeaturePermissions, withRouter, wireAuthConsumer, _WrappedComponent => props => {
    const {
      flowId: stringId
    } = props.overrideParams || props.match.params;
    const flowId = Number(stringId);
    return /*#__PURE__*/_jsx(_WrappedComponent, Object.assign({}, props, {
      flowId: flowId
    }));
  }, wireFlowById, withFlpAccess, connect((state, {
    location,
    match,
    overrideParams,
    overrideParsedQuery = {},
    showEnrollmentCardInEmbed,
    hasScope,
    flow,
    flowId
  }) => {
    const {
      actionId: selectedActionId = null,
      actionType: selectedActionTypePath = null
    } = overrideParams || match.params;
    const isEmbedded = getIsEmbedded(state);
    const parsedQuery = parseLocation(location);
    const embeddedSelectedActionId = overrideParsedQuery.actionId || parsedQuery.actionId;
    const isNew = Boolean(overrideParsedQuery.isNew || parsedQuery.isNew);
    const workflowId = flow && flow.getWorkflowId();
    const highlightedActionIds = getHighlightedActionIds(state);
    const flowValidation = getFlowValidation(flowId)(state);
    const selectedActionMetadataConfig = selectedActionTypePath ? basePathToActionMetadataConfig(selectedActionTypePath, parsedQuery) : null;
    const hasWorkflowsAppAccess = hasScope(Scopes.WORKFLOWS_APP_ACCESS);
    return {
      flowValidationObjectRequestStatus: getFlowValidationObjectRequestStatus(state),
      flow,
      flowId,
      workflowId,
      flowValidation,
      sourceApp: getSourceApp(state),
      selectedActionId: isEmbedded ? embeddedSelectedActionId : selectedActionId,
      showEnrollmentCardInEmbed,
      selectedActionMetadataConfig,
      embedId: getEmbedId(state),
      edgeId: parsedQuery.edgeId,
      portalId: state.auth.portal.portal_id,
      isEmbedded,
      isNew,
      activeFlowId: Number(parsedQuery.activeFlowId),
      columnWidth: parsedQuery.columnWidth ? Number(parsedQuery.columnWidth) : undefined,
      highlightedActionIds,
      shouldPollBulkProcessing: getShouldPollBulkProcessing(flowId)(state),
      hasWorkflowsAppAccess,
      showEdgeTooltips: true,
      isCanvasVirtualRenderingDisabled: getIsCanvasVirtualRenderingDisabled(state),
      zoomLevel: getZoomLevel(flowId)(state)
    };
  }, dispatch => ({
    dispatch
  })), wireTemplate, wireUpdateFlow, queryObjectsInActionsPolling, _WrappedComponent => props => wireActionOutputs(_WrappedComponent, {
    flow: props.flow
  })(props), queryAllExtensionDefinitions // Requires flowSourceApp and flowObjectTypeId
  )(FlowCanvasFull);
  ConnectedFlowCanvasFull.displayName = `ConnectedFlowCanvasFull(${componentName})`;
  attachWrappedComponent(ConnectedFlowCanvasFull, WrappedComponent);
  return ConnectedFlowCanvasFull;
}